// CSV + Excel export for the Bank Account History register.
// CSV is UTF-8 with BOM so Excel opens it with the right encoding.
// Excel uses the xlsx (SheetJS) package via dynamic import so the bundle only
// pulls it in when the user actually clicks the Excel option.

import type { BankTransaction } from "./types";
import type { ColumnLayout } from "./constants";

export interface ExportRow extends BankTransaction {
  runningBalance: number;
}

export interface ExportMeta {
  accountName: string;
  accountId: string;
  accountType: string;
  layout: ColumnLayout;
}

function escapeCsvValue(v: unknown): string {
  if (v == null) return "";
  const s = String(v);
  if (/[",\n\r]/.test(s)) {
    return `"${s.replace(/"/g, '""')}"`;
  }
  return s;
}

function fmtDateForExport(iso: string): string {
  // Novala standing date format: DD/MM/YYYY.
  const parts = iso.split("-");
  if (parts.length === 3) return `${parts[2]}/${parts[1]}/${parts[0]}`;
  return iso;
}

function labelForType(type: string): string {
  const map: Record<string, string> = {
    payment: "Payment",
    cheque: "Cheque",
    deposit: "Deposit",
    transfer: "Transfer",
    debit: "Debit",
    payroll: "Payroll",
    interest: "Interest",
    "bank-fee": "Bank fee",
  };
  return map[type] ?? type;
}

function statusLabel(s: BankTransaction["reconcileStatus"]): string {
  if (s === "reconciled") return "Reconciled";
  if (s === "cleared") return "Cleared";
  return "Unreconciled";
}

function buildFilename(meta: ExportMeta, rows: ExportRow[], ext: "csv" | "xlsx"): string {
  const slug = meta.accountName.replace(/[^A-Za-z0-9]+/g, "-").replace(/^-|-$/g, "");
  const dates = rows.map((r) => r.date).sort();
  const first = dates[0] ?? "export";
  const last = dates[dates.length - 1] ?? "export";
  return `${slug}_${first}_to_${last}.${ext}`;
}

function buildGrid(rows: ExportRow[], meta: ExportMeta): (string | number)[][] {
  const headers: (string | number)[] = [
    "Date",
    "Ref No",
    "Type",
    "Payee",
    "Account",
    "Memo",
    meta.layout.leftLabel,
    meta.layout.rightLabel,
    "Status",
    meta.layout.balanceLabel,
  ];
  const data = rows.map((r) => {
    const leftVal = r[meta.layout.leftFieldKey];
    const rightVal = r[meta.layout.rightFieldKey];
    return [
      fmtDateForExport(r.date),
      r.refNo ?? "",
      labelForType(r.type),
      r.payee ?? "",
      r.linkedAccountName ?? "",
      r.memo ?? "",
      leftVal != null ? Number(leftVal) : "",
      rightVal != null ? Number(rightVal) : "",
      statusLabel(r.reconcileStatus),
      Number(r.runningBalance),
    ] as (string | number)[];
  });
  return [headers, ...data];
}

function downloadBlob(content: BlobPart, filename: string, mimeType: string): void {
  const blob = new Blob([content], { type: mimeType });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  setTimeout(() => URL.revokeObjectURL(url), 1000);
}

export function exportAsCsv(rows: ExportRow[], meta: ExportMeta): void {
  const grid = buildGrid(rows, meta);
  const BOM = "\uFEFF";
  const content = BOM + grid.map((row) => row.map(escapeCsvValue).join(",")).join("\r\n");
  const filename = buildFilename(meta, rows, "csv");
  downloadBlob(content, filename, "text/csv;charset=utf-8");
}

export async function exportAsXlsx(rows: ExportRow[], meta: ExportMeta): Promise<void> {
  let XLSX: typeof import("xlsx");
  try {
    XLSX = await import("xlsx");
  } catch (err) {
    console.error("xlsx package is not installed", err);
    alert("Excel export is unavailable: run `npm install xlsx` and reload the page, or use CSV instead.");
    return;
  }

  const grid = buildGrid(rows, meta);
  const title = `Account history: ${meta.accountName} (Account ${meta.accountId}, ${meta.accountType})`;
  const aoa: (string | number)[][] = [[title], [], ...grid];
  const sheet = XLSX.utils.aoa_to_sheet(aoa);
  // Auto column widths based on the widest value in each column
  const colWidths = grid[0].map((_, colIdx) => {
    const maxLen = Math.max(...aoa.map((r) => String(r[colIdx] ?? "").length));
    return { wch: Math.min(Math.max(maxLen + 2, 10), 40) };
  });
  (sheet as unknown as { [k: string]: unknown })["!cols"] = colWidths;
  const wb = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(wb, sheet, "Account history");
  XLSX.writeFile(wb, buildFilename(meta, rows, "xlsx"));
}